import { action, observable } from 'mobx';
import { IEnvironment } from '../../../interfaces/IEnvironment';
import { IFeed, IFeedStructureItem } from '../../../interfaces/IFeed';

export class FeedAdminStore {
  @observable public highlightColor: string = "#23879b";

  @observable public environment: IEnvironment;
  @observable public tenant: string;
  @observable public token: string = "00000000-0000-0000-0000-000000000000";

  @observable public selectedApplication: "messages" | "events" = "messages";

  @observable public isLoadingFeeds: boolean = true;
  @observable public feeds: IFeed[];
  
  @observable public feedToEdit: IFeed;
  @observable public feedToDelete: IFeedStructureItem;
  @observable public headerToEdit: IFeedStructureItem;
  
  @observable public isStartingFromScratch: boolean = false;
  @observable public isSavingStructure: boolean;
  @observable public structuredFeedsBeforeChanges: IFeedStructureItem[] = [];
  @observable public structuredFeeds: IFeedStructureItem[] = [];
  @observable public unstructuredFeedsBeforeChanges: IFeedStructureItem[] = [];
  @observable public unstructuredFeeds: IFeedStructureItem[] = [];

  @observable public parentToAddFeedsTo: IFeedStructureItem;
  @observable public parentToAddHeaderTo: IFeedStructureItem;
  @observable public isAddingTopLevelGroupHeader: boolean;
  @observable public isAddingTopLevelFeed: boolean;

  @action public resetStore() {
    this.feeds = [];
    this.structuredFeedsBeforeChanges = [];
    this.structuredFeeds = [];
    this.unstructuredFeedsBeforeChanges = [];
    this.unstructuredFeeds = [];
  }

  @action public moveItem(itemToMove: IFeedStructureItem, direction: "up" | "down") {
    this.structuredFeeds = this.moveItemRecursively(itemToMove, this.structuredFeeds, direction);
  }

  @action public moveItemOut(itemToMove: IFeedStructureItem) {
    this.structuredFeeds = this.moveItemOutRecursively(itemToMove, this.structuredFeeds);
  }

  @action public setHighlightForItemInGroups(itemToEdit: IFeedStructureItem) {
    this.structuredFeeds = this.setHighlightForItemInGroupsRecursively(itemToEdit, this.structuredFeeds);
  }

  @action public deleteItemInGroups(itemToDelete: IFeedStructureItem) {
    const deleteItemResult = this.deleteItemInGroupsRecursively(itemToDelete, this.structuredFeeds);
    this.structuredFeeds = deleteItemResult.structure;
    this.unstructuredFeeds.push(...this.flattenStructure(deleteItemResult.removedItems));
  }

  @action public flattenStructure(structuredFeeds: IFeedStructureItem[]): IFeedStructureItem[] {
    const result: IFeedStructureItem[] = [];
    flattenStructureRecursively(structuredFeeds);
    return result;
  
    function flattenStructureRecursively(structuredFeeds: IFeedStructureItem[]) {
      structuredFeeds.forEach(feed => {
        if (feed.id) {
          result.push(feed);
        }
        if (feed.children?.length > 0) {
          flattenStructureRecursively(feed.children);
        }
      });
    }
  }

  private moveItemRecursively(itemToMove: IFeedStructureItem, structure: IFeedStructureItem[], direction: "up" | "down"): IFeedStructureItem[] {
    if (this.structuredFeeds.length > 0) {
      let oldIndex;
      let newIndex;
      structure.forEach((feed, index) => {
        if (feed?.children?.length > 0) {
          feed.children = this.moveItemRecursively(itemToMove, feed?.children, direction);
        }
        if (feed === itemToMove) {
          oldIndex = index;
          newIndex = direction === "up" ? index - 1 : index + 1;
        }
      });
      if (oldIndex != undefined) {
        structure.splice(oldIndex, 1);
        structure.splice(newIndex, 0, itemToMove);
      }
    }
    return structure;
  }

  private moveItemOutRecursively(itemToMoveOut: IFeedStructureItem, structure: IFeedStructureItem[], parentGroup?: IFeedStructureItem[]): IFeedStructureItem[] {
    if (structure.length > 0) {
      structure.forEach((feed, index) => {
        if (feed?.children?.length > 0) {
          feed.children = this.moveItemOutRecursively(itemToMoveOut, feed?.children, structure);
        }
        if (feed === itemToMoveOut) {
          structure.splice(index, 1);
          parentGroup.push(itemToMoveOut);
        }
      })
    }
    return structure;
  }

  private setHighlightForItemInGroupsRecursively(itemToEdit: IFeedStructureItem, structure: IFeedStructureItem[]): IFeedStructureItem[] {
    if (structure.length > 0) {
      structure.forEach((feed) => {
        if (feed?.children?.length > 0) {
          feed.children = this.setHighlightForItemInGroupsRecursively(itemToEdit, feed?.children);
        }
        if (feed.id === itemToEdit.id) {
          feed.highlighted = itemToEdit.highlighted == undefined ? true : !itemToEdit.highlighted;
        }
      })
    }
    return structure;
  }

  private deleteItemInGroupsRecursively(itemToDelete: IFeedStructureItem, structure: IFeedStructureItem[], removedItems: IFeedStructureItem[] = []): { structure: IFeedStructureItem[], removedItems: IFeedStructureItem[] } {
    if (structure.length > 0) {
      structure.forEach((feed, index) => {
        if (feed?.children?.length > 0) {
          feed.children = this.deleteItemInGroupsRecursively(itemToDelete, feed?.children, removedItems).structure;
        }
        if (feed === itemToDelete) {
          const item = structure.splice(index, 1)[0];
          removedItems.push(item);
        }
      });
    }
    return {
      structure,
      removedItems
    };
  }
}
